import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

export type SortDirection = "asc" | "desc";

export interface SortState<Key extends string> {
  direction: SortDirection;
  key: Key;
}

export interface TableColumn<Key extends string> {
  defaultDirection: SortDirection;
  key: Key;
  label: string;
  minWidth: number;
  width: number;
}

type ColumnWidths<Key extends string> = Record<Key, number>;

function defaultWidths<Key extends string>(columns: readonly TableColumn<Key>[]): ColumnWidths<Key> {
  return Object.fromEntries(columns.map((column) => [column.key, column.width])) as ColumnWidths<Key>;
}

function restoreWidths<Key extends string>(
  columns: readonly TableColumn<Key>[],
  storageKey: string,
): ColumnWidths<Key> {
  const fallback = defaultWidths(columns);
  if (typeof window === "undefined") return fallback;
  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey) ?? "{}") as Record<string, unknown>;
    return Object.fromEntries(columns.map((column) => {
      const value = stored[column.key];
      return [
        column.key,
        typeof value === "number" && Number.isFinite(value)
          ? Math.max(column.minWidth, Math.round(value))
          : fallback[column.key],
      ];
    })) as ColumnWidths<Key>;
  } catch {
    return fallback;
  }
}

export function useResizableColumns<Key extends string>(
  columns: readonly TableColumn<Key>[],
  storageKey: string,
) {
  const [widths, setWidths] = useState<ColumnWidths<Key>>(() => restoreWidths(columns, storageKey));
  const stopResizeRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(widths));
  }, [storageKey, widths]);

  useEffect(() => () => stopResizeRef.current?.(), []);

  const resizeBy = (key: Key, delta: number) => {
    const column = columns.find((item) => item.key === key);
    if (!column) return;
    setWidths((current) => ({
      ...current,
      [key]: Math.max(column.minWidth, current[key] + delta),
    }));
  };

  const startResize = (key: Key, event: ReactPointerEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    stopResizeRef.current?.();

    const startX = event.clientX;
    const startWidth = widths[key];
    const column = columns.find((item) => item.key === key);
    if (!column) return;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const move = (pointerEvent: PointerEvent) => {
      setWidths((current) => ({
        ...current,
        [key]: Math.max(column.minWidth, Math.round(startWidth + pointerEvent.clientX - startX)),
      }));
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      stopResizeRef.current = undefined;
    };
    stopResizeRef.current = stop;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
  };

  return {
    resizeBy,
    startResize,
    totalWidth: columns.reduce((total, column) => total + widths[column.key], 0),
    widths,
  };
}

export function toggleSort<Key extends string>(
  current: SortState<Key> | undefined,
  key: Key,
  defaultDirection: SortDirection,
): SortState<Key> {
  if (current?.key !== key) return { direction: defaultDirection, key };
  return { direction: current.direction === "desc" ? "asc" : "desc", key };
}

type SortableValue = number | string | undefined;

export function compareTableValues(
  left: SortableValue,
  right: SortableValue,
  direction: SortDirection,
): number {
  if (left === undefined && right === undefined) return 0;
  if (left === undefined) return 1;
  if (right === undefined) return -1;
  const result = typeof left === "number" && typeof right === "number"
    ? left - right
    : String(left).localeCompare(String(right), "ru", { numeric: true, sensitivity: "base" });
  return direction === "asc" ? result : -result;
}

export function SortableHeader<Key extends string>({
  column,
  currentWidth,
  onResizeBy,
  onResizeStart,
  onSort,
  sort,
}: {
  column: TableColumn<Key>;
  currentWidth: number;
  onResizeBy: (key: Key, delta: number) => void;
  onResizeStart: (key: Key, event: ReactPointerEvent<HTMLElement>) => void;
  onSort: (key: Key, defaultDirection: SortDirection) => void;
  sort?: SortState<Key>;
}) {
  const active = sort?.key === column.key;
  const onResizerKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    event.stopPropagation();
    onResizeBy(column.key, event.key === "ArrowLeft" ? -8 : 8);
  };

  return (
    <th
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      className="sortable-table-header"
      data-column-key={column.key}
      scope="col"
    >
      <button
        className="table-header-button"
        onClick={() => onSort(column.key, column.defaultDirection)}
        type="button"
      >
        {active && <span aria-hidden="true" className="table-sort-arrow">{sort.direction === "asc" ? "↑" : "↓"}</span>}
        <span>{column.label}</span>
      </button>
      <span
        aria-label={`Изменить ширину колонки ${column.label}`}
        aria-orientation="vertical"
        aria-valuemin={column.minWidth}
        aria-valuenow={currentWidth}
        className="column-resizer"
        data-column-resizer={column.key}
        onKeyDown={onResizerKeyDown}
        onPointerDown={(event) => onResizeStart(column.key, event)}
        role="separator"
        tabIndex={0}
      />
    </th>
  );
}
